import React, { useState, useCallback, useMemo, useRef } from "react";
import { Box, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import MenuItem from "./Menu/MenuItem";
import SubLevel from "./Menu/SubLevel";
import { apiURL } from "../../../../config/default";
import axios from 'axios';
import setAuthHeader from "../../../../utils/setAuthHeader";

const MenuList = ({
  menuData,
  setMenuData,
  onEdit,
  onDelete,
  onColorEdit,
  handleAddClick,
  onAddSubmenu,
  setSubMenuChild,
  onPositionEdit,
}) => {
  // Memoize the initial visibility state calculation
  const initializeVisibilityState = useCallback(
    (data) => ({
      menu: data.map((menu) => menu.isVisible ?? true),
      submenu: data.map((menu) => ({
        items: menu?.submenu?.map((submenu) => submenu.isVisible ?? true),
        variations: menu?.submenu?.map((submenu) =>
          submenu?.variations?.map((variation) => variation.isVisible ?? true)
        ),
        spotItems: menu?.submenu?.map((submenu) =>
          submenu?.spotItems?.map((spotItem) => spotItem.isVisible ?? true)
        ),
      })),
    }),
    []
  );

  const [visibilityState, setVisibilityState] = useState(() =>
    initializeVisibilityState(menuData)
  );

  const [expandedStates, setExpandedStates] = useState({
    menu: null,
    subMenu: null,
  });

  // Memoize API endpoint URL
  const API_ENDPOINT = useMemo(
    () => `${apiURL}header/header`,
    []
  );

  // Ref to store the timeout for API call
  const apiCallTimeoutRef = useRef(null);

  const handleMove = useCallback(
    (draggedItem, dropTarget) => {
      const {
        id: draggedId,
        level: dragLevel,
        levelIndex: dragLevelIndex,
      } = draggedItem;
      const {
        index: dropIndex,
        level: dropLevel,
        levelIndex: dropLevelIndex,
      } = dropTarget;

      setMenuData((prevData) => {
        const newData = JSON.parse(JSON.stringify(prevData));

        const updateItemsAtLevel = (data, level, levelIndex) => {
          if (level === 0) {
            // Top-level menu reordering (existing code)
            const [removed] = data.splice(draggedItem.index, 1);
            data.splice(dropIndex, 0, removed);
            data.forEach((item, index) => {
              item.position = index + 1;
            });
          } else if (level === 1) {
            // Submenu reordering
            const menu = data[expandedStates.menu];
            if (menu?.submenu) {
              const [removed] = menu.submenu.splice(draggedItem.index, 1);
              menu.submenu.splice(dropIndex, 0, removed);
              menu.submenu.forEach((item, index) => {
                item.position = index + 1;
              });
            }
          } else if (level === 2) {
            // Variations or spot items reordering
            const menu = data[expandedStates.menu];
            const submenu = menu?.submenu[expandedStates.subMenu];
            if (submenu) {
              const targetArray =
                levelIndex === 1 ? submenu.variations : submenu.spotItems;
              if (targetArray) {
                const [removed] = targetArray.splice(draggedItem.index, 1);
                targetArray.splice(dropIndex, 0, removed);
                targetArray.forEach((item, index) => {
                  item.position = index + 1;
                });
              }
            }
          }
          return data;
        };

        const updatedData = updateItemsAtLevel(
          newData,
          dragLevel,
          dragLevelIndex
        );

        // Clear any existing timeout
        if (apiCallTimeoutRef.current) {
          clearTimeout(apiCallTimeoutRef.current);
        }

        // Set a new timeout for API call
        apiCallTimeoutRef.current = setTimeout(() => {
          saveToAPI(updatedData);
        }, 2000);

        return updatedData;
      });
    },
    [expandedStates, setMenuData]
  );

  const saveToAPI = async (updatedData) => { 
    console.log('herere')
     const token = localStorage.getItem("token");
  if (token) {
    setAuthHeader(token);
  }
    try {
    const response = await axios.post(API_ENDPOINT, updatedData, {
      headers: {
         "Content-Type": "application/json" ,
        "x-auth-token": token
      },
    });
    console.log("Menu order saved successfully:", response.data);
  } catch (error) {
    if (error.response) {
      console.error(`Failed to save menu order: ${error.response.status} - ${error.response.statusText}`);
    } else if (error.request) {
      console.error("Error saving menu order: No response received from the server.");
    } else {
      console.error("Error saving menu order:", error.message);
    }
  }
  };
  const handleVisibilityToggle = async (
    level,
    parentIndices,
    type = "menu"
  ) => {
    try {
      // Deep copy visibilityState and menuData only when changes are made
      const newVisibilityState = JSON.parse(JSON.stringify(visibilityState));
      const updatedMenuData = JSON.parse(JSON.stringify(menuData));

      // Toggle visibility based on hierarchy level
      switch (level) {
        case 0: {
          const menuIndex = parentIndices[0];
          newVisibilityState.menu[menuIndex] =
            !newVisibilityState.menu[menuIndex];
          updatedMenuData[menuIndex].isVisible =
            newVisibilityState.menu[menuIndex];
          break;
        }
        case 1: {
          const [menuIndex, submenuIndex] = parentIndices;
          newVisibilityState.submenu[menuIndex].items[submenuIndex] =
            !newVisibilityState.submenu[menuIndex].items[submenuIndex];
          updatedMenuData[menuIndex].submenu[submenuIndex].isVisible =
            newVisibilityState.submenu[menuIndex].items[submenuIndex];
          break;
        }
        case 2: {
          const [menuIndex, submenuIndex, itemIndex] = parentIndices;
          newVisibilityState.submenu[menuIndex][type][submenuIndex][itemIndex] =
            !newVisibilityState.submenu[menuIndex][type][submenuIndex][
              itemIndex
            ];
          updatedMenuData[menuIndex].submenu[submenuIndex][type][
            itemIndex
          ].isVisible =
            newVisibilityState.submenu[menuIndex][type][submenuIndex][
              itemIndex
            ];
          break;
        }
        default:
          console.warn(`Unsupported level: ${level}`);
          return;
      }
      // console.log(updatedMenuData, `==============`);
      // Set the updated visibility state
      setVisibilityState(newVisibilityState);
      setMenuData(updatedMenuData);

      // Prepare and send the POST request
      const response = await axios.post(API_ENDPOINT,  updatedMenuData, );

      // Check if the response is OK
      if (!response.ok) {
        console.error(
          `Failed to save visibility state: ${response.statusText}`
        );
      }
    } catch (error) {
      console.error("Error updating visibility state:", error);
    }
  };

  const handleExpand = useCallback((level, index) => {
    setExpandedStates((prev) => {
      const key = level === 0 ? "menu" : "subMenu";

      // Ensure it does not toggle off the current expanded item
      return {
        ...prev,
        [key]: prev[key] === index ? index : index, // Always set the current index
        ...(level === 0 && { subMenu: 0 }), // Reset subMenu if it's the main menu
      };
    });
  }, []);

  const getVisibilityState = useCallback(
    (level, parentIndices, type = "menu") => {
      switch (level) {
        case 0:
          return visibilityState.menu?.[parentIndices[0]] ?? true;
        case 1:
          return (
            visibilityState.submenu?.[parentIndices[0]]?.items?.[
              parentIndices[1]
            ] ?? true
          );
        case 2:
          return (
            visibilityState.submenu?.[parentIndices[0]]?.[type]?.[
              parentIndices[1]
            ]?.[parentIndices[2]] ?? true
          );
        default:
          return true;
      }
    },
    [visibilityState]
  );

  // Update renderMenuItems to include onMove prop
  const renderMenuItems = useCallback(
    (items, level, parentIndices = [], levelIndex = null) => {
      if (!items) return null;

      items = items.sort((a, b) => a.position - b.position);

      return items.map((item, index) => {
        const currentIndices = [...parentIndices, index];
        const isExpanded =
          level <= 1
            ? expandedStates[level === 0 ? "menu" : "subMenu"] === index
            : false;
        const canExpand = level <= 1;
        const type =
          level === 2
            ? levelIndex === 1
              ? "variations"
              : "spotItems"
            : "menu";
        const isVisible = getVisibilityState(level, currentIndices, type);

        return (
          <MenuItem
            key={`${level}-${index}-${item.id || index}`}
            itemIndex={index}
            item={item}
            level={level}
            levelIndex={levelIndex}
            isExpanded={isExpanded}
            isVisible={isVisible}
            currentIndices={currentIndices}
            onItemClick={() => canExpand && handleExpand(level, index)}
            onEdit={onEdit}
            onVisibilityToggle={() =>
              handleVisibilityToggle(level, currentIndices, type)
            }
            onColorEdit={onColorEdit}
            onDelete={onDelete}
            onAddSubmenu={onAddSubmenu}
            canExpand={canExpand}
            onPositionEdit={onPositionEdit}
            onMove={handleMove} // Add the onMove prop here
            sx={{
              cursor: "grab", // Hand cursor for draggable items
              transition: "all 0.3s ease", // Smooth transition
              "&:active": {
                cursor: "grabbing", // Change cursor when actively dragging
              },
            }}
          />
        );
      });
    },
    [
      expandedStates,
      getVisibilityState,
      handleExpand,
      handleVisibilityToggle,
      onEdit,
      onColorEdit,
      onDelete,
      onAddSubmenu,
      onPositionEdit,
      handleMove, // Add handleMove to dependencies
    ]
  );

  const renderSubLevels = useMemo(() => {
    const { menu, subMenu } = expandedStates;
    if (menu === null || !menuData[menu]?.submenu) return null;

    const selectedMenu = menuData[menu];
    const currentSubmenu = selectedMenu?.submenu[subMenu];

    const sortedSubmenu = [...selectedMenu.submenu].sort(
      (a, b) => a.position - b.position
    );

    const sortedVariations = currentSubmenu?.variations
      ? [...currentSubmenu.variations].sort((a, b) => a.position - b.position)
      : [];

    return (
      <>
        <SubLevel
          data={sortedSubmenu}
          level={1}
          title={selectedMenu?.title}
          parentIndices={[menu]}
          levelIndex={0}
          renderMenuItems={renderMenuItems}
          onMove={handleMove} // Add this line
        />
        {subMenu !== null && (
          <>
            <SubLevel
              data={sortedVariations}
              level={2}
              title="Variations"
              parentIndices={[menu, subMenu]}
              levelIndex={1}
              renderMenuItems={renderMenuItems}
              onMove={handleMove} // Add this line
            />
            <SubLevel
              data={currentSubmenu?.spotItems}
              level={2}
              title="Spot Items"
              parentIndices={[menu, subMenu]}
              levelIndex={2}
              renderMenuItems={renderMenuItems}
              onMove={handleMove} // Add this line
            />
          </>
        )}
      </>
    );
  }, [expandedStates, menuData, renderMenuItems, handleMove]);

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
            padding: "10px 5px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              borderRadius: "5px",
              flex: 1,
            }}
          >
            {renderMenuItems(menuData, 0)}
          </Box>
          <IconButton
            onClick={handleAddClick}
            sx={{
              bgcolor: "#4CAF50",
              color: "white",
              "&:hover": {
                bgcolor: "#45a049",
              },
              zIndex: 1,
              width: 40,
              height: 40,
            }}
          >
            <AddIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>

        {expandedStates.menu !== null && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr 1fr",
              overflowX: "auto",
              width: "100%",
            }}
          >
            {renderSubLevels}
          </Box>
        )}
      </Box>
   </DndProvider>
  );
};

export default MenuList;
